/**
 * The ONE decider behind the wizard's autosave indicator (SOR-355 / R17).
 *
 * The indicator answers "is my draft persisted?", and the answer comes from
 * the OUTCOME OF THE LAST SAVE — never from a timer. Both former surfaces (the
 * breadcrumb badge, the Filing Summary rail) used to print a message that a
 * 3-second setTimeout cleared, after which a persisted draft read "Not saved"
 * again; this table replaces that, and no timer may write the indicator.
 *
 * "Changed since save" is reference identity of the saved `wizardState`,
 * resolved by `EFilingProvider` (every writer produces a new object through
 * `setWizardState`), so this module stays pure and dependency-free: it sees
 * only the resolved inputs.
 */
export type SaveOutcome = 'none' | 'succeeded' | 'failed';

export type SaveIndicatorState = 'unsaved' | 'saved' | 'failed';

export interface SaveIndicatorInputs {
  /** Outcome of the most recent save attempt (autosave or either Save Draft button). */
  lastSaveOutcome: SaveOutcome;
  /** Whether the wizard state has been replaced since that save. */
  changedSinceSave: boolean;
}

export function saveIndicatorState({
  lastSaveOutcome,
  changedSinceSave,
}: SaveIndicatorInputs): SaveIndicatorState {
  // A failed save is sticky: it reads failed even while the filer keeps
  // editing, until the next save attempt records a new outcome. The badge must
  // not soften to "Not saved" (the save did not merely go missing — it failed)
  // nor claim "Saved" (it did not save).
  if (lastSaveOutcome === 'failed') return 'failed';
  if (lastSaveOutcome === 'succeeded' && !changedSinceSave) return 'saved';
  return 'unsaved';
}

/**
 * The ONE state-to-text catalog — every surface that prints the indicator's
 * state (the breadcrumb badge, the Filing Summary rail) reads these labels, so
 * a copy edit lands on both at once and no hand-maintained second catalog can
 * diverge (the `FEE_WAIVER_TAG_LABEL` precedent: one label, read by every
 * surface, pinned by a lib test). Exact strings: "Not saved" contains
 * "saved", so tests assert whole text and E2E locates
 * `wizard-save-indicator` rather than a substring.
 */
export const SAVE_INDICATOR_LABEL: Record<SaveIndicatorState, string> = {
  saved: 'Saved',
  failed: 'Save failed',
  unsaved: 'Not saved',
};
